import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getPandits, deletePandit, createPandit, updatePandit } from "../../../utils/api";

const columns = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { 
    key: "status", 
    label: "Status",
    render: (value) => (
      <span className={`status-badge status-${value?.toLowerCase()}`}>
        {value || "Available"}
      </span>
    )
  },
  { 
    key: "type", 
    label: "Type",
    render: (value) => (
      <span className="type-badge">
        {value || "Pandit"}
      </span>
    )
  },
  { 
    key: "skills", 
    label: "Skills",
    render: (value) => value && Array.isArray(value) ? value.slice(0, 3).join(", ") + (value.length > 3 ? "..." : "") : "-"
  },
  { 
    key: "createdOn", 
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "type",
    label: "Filter by Type",
    type: "select",
    options: [
      { value: "Pandit", label: "Pandit" },
      { value: "Pracharak", label: "Pracharak" },
    ]
  },
  {
    key: "status",
    label: "Filter by Status",
    type: "select",
    options: [
      { value: "Available", label: "Available" },
      { value: "Unavailable", label: "Unavailable" },
      { value: "Suspended", label: "Suspended" },
    ]
  },
];

const initialFormData = {
  profilePicture: "",
  firstName: "",
  middleNames: "",
  lastName: "",
  email: "",
  type: "Pandit",
  birthDate: "",
  birthPlace: {
    city: "",
    country: ""
  },
  address: "",
  phone: "",
  mobile: "",
  idNumber: "",
  status: "Available",
  skills: []
};

export default function Pandits() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPandit, setSelectedPandit] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [skillInput, setSkillInput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setSkillInput("");
    setIsEditMode(false);
    setSelectedPandit(null);
    setModalOpen(true);
  };

  const handleEdit = (pandit) => {
    setSelectedPandit(pandit);
    setFormData({
      profilePicture: pandit.profilePicture || "",
      firstName: pandit.firstName || "",
      middleNames: pandit.middleNames || "",
      lastName: pandit.lastName || "",
      email: pandit.email || "",
      type: pandit.type || "Pandit",
      birthDate: pandit.birthDate ? new Date(pandit.birthDate).toISOString().split('T')[0] : "",
      birthPlace: pandit.birthPlace || { city: "", country: "" },
      address: pandit.address || "",
      phone: pandit.phone || "",
      mobile: pandit.mobile || "",
      idNumber: pandit.idNumber || "",
      status: pandit.status || "Available",
      skills: pandit.skills || []
    });
    setSkillInput("");
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (pandit) => {
    handleEdit(pandit);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("birthPlace.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({ 
        ...prev, 
        birthPlace: { ...prev.birthPlace, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Compress image
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, profilePicture: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && selectedPandit) {
        await updatePandit(selectedPandit._id, formData);
        alert("Pandit updated successfully!");
      } else {
        await createPandit(formData);
        alert("Pandit created successfully!");
      }
      setModalOpen(false);
      window.location.reload();
    } catch (error) {
      alert(error.message || "Failed to save pandit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Pandits"
          fetchData={getPandits}
          deleteData={deletePandit}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search pandits by name, email or phone..."
        />
      </main>

      {/* Create/Edit Pandit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Pandit" : "Add New Pandit"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Middle Names</label>
                <input
                  type="text"
                  name="middleNames"
                  value={formData.middleNames}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  required
                >
                  <option value="Pandit">Pandit</option>
                  <option value="Pracharak">Pracharak</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  required
                >
                  <option value="Available">Available</option>
                  <option value="Unavailable">Unavailable</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Personal Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Birth Date</label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>ID Number</label>
                <input
                  type="text"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Birth City</label>
                <input
                  type="text"
                  name="birthPlace.city"
                  value={formData.birthPlace.city}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>Birth Country</label>
                <input
                  type="text"
                  name="birthPlace.country"
                  value={formData.birthPlace.country}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleFormChange}
                rows="2"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleFormChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Skills</h3>
            <div className="form-group">
              <label>Add Skills</label>
              <div style={{ display: "flex", gap: "10px" }}>
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="Enter a skill"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                  style={{ flex: 1 }}
                />
                <button type="button" onClick={handleAddSkill} className="btn btn-secondary">
                  Add
                </button>
              </div>
              <div className="skills-list" style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "10px" }}>
                {formData.skills.map((skill, index) => (
                  <span key={index} className="skill-tag" style={{ 
                    background: "#e0e0e0", 
                    padding: "5px 10px", 
                    borderRadius: "15px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px"
                  }}>
                    {skill}
                    <button 
                      type="button" 
                      onClick={() => handleRemoveSkill(index)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Profile Picture</h3>
            <div className="form-group">
              <label>Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.profilePicture && (
                <div className="image-preview">
                  <img src={formData.profilePicture} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, profilePicture: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Pandit" : "Create Pandit")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
